// src/runtime/index.ts
import type { LoggerRuntime, RuntimeContext } from "./types";
import { isClient } from "./detect";

class ServerRuntime implements LoggerRuntime {
  getContext(): RuntimeContext {
    return {
      runtime: "server",
      isFileSystemAvailable: true,
      isProcessAvailable: true,
      environment:
        (process.env["NODE_ENV"] as "development" | "production" | "test") ||
        "development",
    };
  }

  getEnvVar(name: string): string | undefined {
    return process.env[name];
  }

  setupErrorHandlers(logger: any): void {
    if (typeof process !== "undefined") {
      // Handle uncaught exceptions
      process.on("uncaughtException", (error) => {
        logger.fatal("Uncaught Exception:", error);
        process.exit(1);
      });

      // Handle unhandled rejections
      process.on("unhandledRejection", (reason, promise) => {
        logger.error("Unhandled Rejection at:", promise, "reason:", reason);
      });

      // Graceful shutdown handlers
      const signals = ["SIGTERM", "SIGINT", "SIGUSR2"];

      for (const signal of signals) {
        process.once(signal, async () => {
          logger.info(`Received ${signal}, shutting down...`);
          await logger.end?.();
          process.exit(0);
        });
      }
    }
  }
}

class ClientRuntime implements LoggerRuntime {
  getContext(): RuntimeContext {
    return {
      runtime: "client",
      isFileSystemAvailable: false,
      isProcessAvailable: false,
      environment:
        (process.env?.["MODE"] as "development" | "production" | "test") ||
        "development",
    };
  }

  getEnvVar(name: string): string | undefined {
    return process.env?.[name];
  }

  setupErrorHandlers(logger: any): void {
    if (typeof window !== "undefined") {
      // Handle unhandled promise rejections
      window.addEventListener("unhandledrejection", (event) => {
        logger.error("Unhandled Rejection:", event.reason);
      });

      // Handle uncaught errors
      window.addEventListener("error", (event) => {
        logger.fatal("Uncaught Error:", event.error);
      });
    }
  }
}

// Runtime singleton instance
let runtimeInstance: LoggerRuntime;

export function getRuntime(): LoggerRuntime {
  if (!runtimeInstance) {
    runtimeInstance = isClient() ? new ClientRuntime() : new ServerRuntime();
  }
  return runtimeInstance;
}

// Convenience exports
export function getEnvVar(name: string): string | undefined {
  return getRuntime().getEnvVar(name);
}

export function getRuntimeContext(): RuntimeContext {
  return getRuntime().getContext();
}

export function setupErrorHandlers(logger: any): void {
  return getRuntime().setupErrorHandlers(logger);
}
